/**
 * Banner de cookies — sem padrão escuro.
 *
 * O que este componente NÃO faz, e é o ponto:
 *
 *   - Não pré-marca categoria opcional. Enquanto a pessoa não escolhe, nada
 *     opcional é gravado. Não existe consentimento presumido por navegação.
 *   - Não esconde "Rejeitar" atrás de "Personalizar". Os três caminhos —
 *     aceitar, rejeitar, personalizar — têm o MESMO destaque visual e o mesmo
 *     número de cliques. Recusar não pode ser mais trabalhoso do que aceitar.
 *   - Não bloqueia a tela. O banner é uma faixa, não um modal: a pessoa pode ler
 *     a política antes de decidir, e um modal a impediria de fazer exatamente
 *     isso.
 *   - Não reaparece a cada carregamento depois de decidido. Insistir até
 *     conseguir o "sim" é a definição de coerção.
 *
 * A escolha é guardada no servidor, e não só no navegador: assim sobrevive à
 * troca de dispositivo e existe prova de quando foi feita.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

interface Categoria {
  chave: string;
  nome: string;
  descricao: string;
  essencial: boolean;
  cookies: { nome: string; finalidade: string; retencao: string }[];
}

interface Estado {
  decidido: boolean;
  categorias: Record<string, boolean>;
  inventario: Categoria[];
}

export function BannerDeCookies(): JSX.Element | null {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [detalhando, setDetalhando] = useState(false);
  const [escolha, setEscolha] = useState<Record<string, boolean>>({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    void api
      .get<Estado>('/legal/cookies')
      .then((e) => {
        setEstado(e);
        setEscolha(e.categorias);
      })
      // Falha ao consultar não pode virar banner permanente nem, pior, gravação
      // por omissão: sem resposta, o banner simplesmente não aparece e nada
      // opcional é ligado.
      .catch(() => setEstado(null));
  }, []);

  const decidir = async (categorias: Record<string, boolean>): Promise<void> => {
    setSalvando(true);
    try {
      await api.put('/legal/cookies', { categorias });
      setEstado((atual) => (atual === null ? null : { ...atual, decidido: true }));
    } finally {
      setSalvando(false);
    }
  };

  const visivel = estado !== null && !estado.decidido;

  /**
   * ENQUANTO O BANNER ESTÁ NA TELA, ELE MARCA O DOCUMENTO.
   *
   * O banner é uma faixa fixa no rodapé, e o acionador do assistente mora no
   * canto inferior direito: um cobria o outro, e clicar no assistente ficava
   * impossível até alguém decidir sobre cookies. Quem chega pela primeira vez —
   * exatamente quem vê o banner — era quem ficava sem o assistente.
   *
   * A marca no `<html>` deixa o deslocamento ser uma regra de estilo, sem
   * espalhar estado de um componente pelo outro. Ela é retirada assim que o
   * banner sai, inclusive ao desmontar.
   */
  useEffect(() => {
    if (visivel) {
      document.documentElement.dataset.bannerDeCookies = 'visivel';
    } else {
      delete document.documentElement.dataset.bannerDeCookies;
    }
    return () => {
      delete document.documentElement.dataset.bannerDeCookies;
    };
  }, [visivel]);

  /**
   * A ALTURA REAL DA FAIXA (CERES-IA-002), em `--altura-banner-cookies`.
   *
   * O deslocamento fixo (9rem) não cabia no celular: a faixa quebra em mais
   * linhas e agora fica acima da navegação inferior, e o acionador do
   * assistente voltava a ficar por baixo dela. Medida, a altura acompanha a
   * quebra de linha, o "Escolher" aberto e a rotação da tela.
   */
  const observador = useRef<ResizeObserver | null>(null);
  const faixaRef = useCallback((elemento: HTMLDivElement | null): void => {
    observador.current?.disconnect();
    observador.current = null;
    const raiz = document.documentElement;
    if (elemento === null) {
      raiz.style.removeProperty('--altura-banner-cookies');
      return;
    }
    const medir = (): void => raiz.style.setProperty('--altura-banner-cookies', `${elemento.offsetHeight}px`);
    medir();
    if (typeof ResizeObserver !== 'undefined') {
      observador.current = new ResizeObserver(medir);
      observador.current.observe(elemento);
    }
  }, []);

  if (!visivel) return null;

  /*
   * O BANNER NÃO PODE DERRUBAR A APLICAÇÃO.
   *
   * Ele mora no `AppShell` e renderiza em TODA tela autenticada. Um inventário
   * ausente na resposta — versão antiga do servidor, payload parcial — lançava
   * aqui, no meio do render, e o React desmontava a árvore acima: a pessoa
   * ficava com a tela em branco por causa do aviso de cookies.
   *
   * Sem categoria para mostrar, o banner simplesmente não aparece; a decisão
   * continua pendente e o aviso volta na próxima leitura, que é o
   * comportamento correto — nunca "aceito por omissão".
   */
  const inventario = estado.inventario ?? [];
  if (inventario.length === 0) return null;

  const opcionais = inventario.filter((c) => !c.essencial);
  const todasLigadas = Object.fromEntries(inventario.map((c) => [c.chave, true]));
  const soEssenciais = Object.fromEntries(inventario.map((c) => [c.chave, c.essencial]));

  return (
    <div
      role="region"
      aria-label="Preferências de cookies"
      /*
       * z-20: ACIMA do conteúdo, ABAIXO dos diálogos.
       *
       * Com z-40 — o mesmo dos modais, e depois deles na ordem do documento — a
       * faixa cobria os botões de TODO formulário em diálogo: "Cadastrar
       * empresa", "Cancelar", "Salvar". O E2E flagrou como clique que nunca
       * acontece; em produção, quem entrava pela primeira vez simplesmente não
       * conseguia cadastrar nada, e nada na tela explicava por quê.
       *
       * O espaço no rodapé já é reservado por regra de estilo, então descer a
       * faixa não a esconde atrás de nada.
       */
      ref={faixaRef}
      /*
       * Acima da navegação inferior (CERES-IA-002): no celular a faixa ficava
       * atrás dela, com os botões cobertos. `--altura-nav-inferior` vem do
       * AppShell (zero a partir de lg).
       */
      className="fixed inset-x-0 bottom-[var(--altura-nav-inferior,0px)] z-20 border-t border-line bg-surface shadow-pop"
    >
      <div className="mx-auto max-w-4xl px-4 py-3">
        <p className="text-sm text-ink">
          Usamos cookies necessários para manter você conectado. Os opcionais só existem se você
          escolher — <strong className="font-semibold">nada foi gravado ainda</strong>.{' '}
          <Link to="/legal/politica-de-cookies" className="font-medium text-brand-700 underline">
            Ler a política
          </Link>
        </p>

        {detalhando && (
          <ul className="mt-3 space-y-2 border-t border-line pt-3">
            {estado.inventario.map((c) => (
              <li key={c.chave} className="flex items-start gap-2">
                <input
                  id={`cookie-${c.chave}`}
                  type="checkbox"
                  className="mt-0.5"
                  checked={c.essencial ? true : (escolha[c.chave] ?? false)}
                  disabled={c.essencial}
                  onChange={(e) => setEscolha((a) => ({ ...a, [c.chave]: e.target.checked }))}
                />
                <label htmlFor={`cookie-${c.chave}`} className="text-xs">
                  <span className="font-medium text-ink">{c.nome}</span>
                  {c.essencial && (
                    <span className="ml-1 text-2xs text-ink-subtle">(sempre ativo)</span>
                  )}
                  <span className="block text-ink-muted">{c.descricao}</span>
                  <span className="mt-0.5 block text-2xs text-ink-subtle">
                    {c.cookies.map((k) => `${k.nome} — ${k.retencao}`).join(' · ')}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        {/*
          Os três botões têm o MESMO peso visual. "Rejeitar" não é um link
          apagado ao lado de um botão colorido — essa assimetria é o padrão
          escuro mais comum em banner de cookies, e a nossa própria política diz
          que não o usamos.
        */}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={salvando}
            onClick={() => void decidir(detalhando ? escolha : todasLigadas)}
          >
            {detalhando ? 'Salvar escolha' : 'Aceitar todos'}
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={salvando}
            onClick={() => void decidir(soEssenciais)}
          >
            Rejeitar não necessários
          </button>
          {!detalhando && opcionais.length > 0 && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setDetalhando(true)}
            >
              Personalizar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
